import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Activity, Shield, ToggleLeft, ToggleRight, Zap, Brain, TrendingUp, Package, Truck, Users, Search as SearchIcon, Share2, AlertTriangle, CheckCircle, Clock, ChevronDown } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function AgentCommandCenterPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agenticScore, setAgenticScore] = useState(0);
  const [decisions, setDecisions] = useState([]);
  const [guardrails, setGuardrails] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const [agentsRes, scoreRes] = await Promise.all([
        apiClient.get('/api/v1/agents'),
        apiClient.get('/api/v1/agents/score'),
      ]);
      setAgents(agentsRes.data.agents || []);
      setAgenticScore(scoreRes.data.score || 0);
      setDecisions(agentsRes.data.decisions || []);
    } catch (err) {
      setAgents([]);
      setAgenticScore(0);
      setDecisions([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = async (agentType, currentStatus) => {
    const endpoint = currentStatus === 'active' ? 'disable' : 'enable';
    try {
      await apiClient.post(`/api/v1/agents/${agentType}/${endpoint}`);
      setAgents(agents.map(a => a.type === agentType ? { ...a, status: currentStatus === 'active' ? 'paused' : 'active' } : a));
      toast.success(`${agentType} agent ${endpoint}d`);
    } catch (err) {
      toast.error(`Failed to ${endpoint} agent`);
    }
  };

  const iconMap = { TrendingUp, Zap, Package, Truck, Users, SearchIcon, Share2, Brain };
  const getIcon = (name) => iconMap[name] || Bot;

  const outcomeColor = { positive: 'text-[#3EF1C7]', negative: 'text-[#F55454]', neutral: 'text-[#FFC857]' };

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Agent Command Center</h1>
            <p className="text-sm text-[#9CB0C7]">Agent Command Center</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF]/10 border border-[#7A5CFF]/30 rounded-lg">
              <Brain className="w-5 h-5 text-[#7A5CFF]" />
              <div>
                <div className="text-xs text-[#9CB0C7]">Agentic Score</div>
                <div className="text-lg font-bold text-[#7A5CFF]">{agenticScore}/100</div>
              </div>
            </div>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Agent Grid */}
        <h3 className="text-white font-semibold mb-4">Active Agents</h3>
        {agents.length === 0 ? (
          <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-10 mb-8 text-center">
            <Bot className="w-10 h-10 text-[#9CB0C7] mx-auto mb-3" />
            <p className="text-[#9CB0C7] mb-4">No agent activity yet — agents activate when you connect your store</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/dashboard/settings" className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] font-medium rounded-lg hover:bg-[#3EF1C7]/90 transition">Connect your store</Link>
              <Link to="/dashboard/business-os" className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">See your Business OS</Link>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {agents.map(agent => {
            const Icon = getIcon(agent.icon);
            return (
              <div key={agent.type} onClick={() => setSelectedAgent(agent)} className={`bg-[#0E1426] border rounded-xl p-4 cursor-pointer transition hover:border-[#7A5CFF]/50 ${selectedAgent?.type === agent.type ? 'border-[#7A5CFF]' : 'border-[#22304A]'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${agent.status === 'active' ? 'bg-[#3EF1C7]/20' : 'bg-[#FFC857]/20'}`}>
                    <Icon className={`w-4 h-4 ${agent.status === 'active' ? 'text-[#3EF1C7]' : 'text-[#FFC857]'}`} />
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); toggleAgent(agent.type, agent.status); }}>
                    {agent.status === 'active' ? <ToggleRight className="w-8 h-8 text-[#3EF1C7]" /> : <ToggleLeft className="w-8 h-8 text-[#9CB0C7]" />}
                  </button>
                </div>
                <h4 className="text-white font-medium text-sm">{agent.name}</h4>
                <div className="flex items-center gap-1 mt-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${agent.status === 'active' ? 'bg-[#3EF1C7] animate-pulse' : 'bg-[#FFC857]'}`} />
                  <span className="text-xs text-[#9CB0C7]">{agent.status} - {agent.last_active}</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-[#9CB0C7]">{(agent.decisions_count || 0).toLocaleString()} decisions</span>
                  <span className="text-xs text-[#3EF1C7]">{agent.accuracy || 0}% accuracy</span>
                </div>
                <p className="text-xs text-[#9CB0C7] mt-2 line-clamp-2">{agent.last_decision}</p>
              </div>
            );
          })}
        </div>
        )}

        {/* Guardrails Section */}
        <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-[#FFC857]" />
            <h3 className="text-white font-semibold">Guardrails Configuration</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Max Price Change', value: '15%', desc: 'Maximum automatic price adjustment per day' },
              { label: 'Inventory Auto-Order Limit', value: '$5,000', desc: 'Maximum auto-purchase order value' },
              { label: 'Deal Discount Cap', value: '30%', desc: 'Maximum discount agents can offer' },
              { label: 'Human Review Threshold', value: 'High Risk', desc: 'When to escalate to human approval' },
              { label: 'Agent Budget (Monthly)', value: '$2,500', desc: 'Total autonomous spending limit' },
              { label: 'Marketplace USDC Limit', value: '$10,000/day', desc: 'Max USDC transactions per day' },
            ].map((g, i) => (
              <div key={i} className="bg-[#0B1120] rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-[#9CB0C7]">{g.label}</span>
                  <span className="text-sm text-[#FFC857] font-mono">{g.value}</span>
                </div>
                <p className="text-[10px] text-[#9CB0C7]/70">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Decisions Feed */}
        <h3 className="text-white font-semibold mb-4">Recent Agent Decisions</h3>
        <div className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
          {decisions.length === 0 && (
            <div className="p-10 text-center">
              <Activity className="w-10 h-10 text-[#9CB0C7] mx-auto mb-3" />
              <p className="text-[#9CB0C7] mb-4">No agent activity yet — agents activate when you connect your store</p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/dashboard/settings" className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] font-medium rounded-lg hover:bg-[#3EF1C7]/90 transition">Connect your store</Link>
                <Link to="/dashboard/business-os" className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">See your Business OS</Link>
              </div>
            </div>
          )}
          {decisions.map((d, i) => (
            <div key={d.id} className={`flex items-start gap-4 p-4 ${i < decisions.length - 1 ? 'border-b border-[#22304A]/50' : ''}`}>
              <div className={`w-2 h-2 rounded-full mt-2 ${outcomeColor[d.outcome] === 'text-[#3EF1C7]' ? 'bg-[#3EF1C7]' : outcomeColor[d.outcome] === 'text-[#F55454]' ? 'bg-[#F55454]' : 'bg-[#FFC857]'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-[#7A5CFF] font-medium">{d.agent}</span>
                  <span className="text-xs text-[#9CB0C7]">{d.action}</span>
                  <span className="text-xs text-[#9CB0C7] ml-auto">{d.timestamp}</span>
                </div>
                <p className="text-sm text-white">{d.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
